import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {setSlackWizardSelectionMethod} from '../../../../../../redux/slices/settings';
import {HiInformationCircle} from 'react-icons/hi';
import SelectedChips from './SelectedChips';
import InfiniteScrollDropdown from "../../../../../../components/common/InfiniteScrollDropdown";
import {WizardOption} from "../../../../types";

interface StrategyMapSelectionProps {
  options: WizardOption[];
  selectedIds: string[];
  labelsMap?: Record<string, string>;
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  onClear: () => void;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onSearch: (term: string) => void;
}

const StrategyMapSelection: React.FC<StrategyMapSelectionProps> = ({
  options,
  selectedIds,
  labelsMap,
  onAdd,
  onRemove,
  onClear,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  onSearch
}) => {
  const dispatch = useDispatch();
  const { slackWizardSelectionMethod: selectionMethod } = useSelector((state: any) => state.settings || {});
  const [pendingActiveSelection, setPendingActiveSelection] = useState<{ limit: number } | null>(null);
  const [previousSelectiveIds, setPreviousSelectiveIds] = useState<string[] | null>(null);

  const filteredOptions = useMemo(() => options.filter((opt) => !selectedIds.includes(opt.value)), [options, selectedIds]);

  const getLabel = (id: string) => labelsMap?.[id] || options.find((o) => o.value === id)?.label || id;

  const visibleSelectedIds = useMemo(() => {
    return selectedIds.filter((id) => Boolean(labelsMap?.[id])) 
  }, [selectedIds, labelsMap]);

  const handleEntitySelect = (value: string) => {
    if (!value) return;
    onAdd(value);
  };

  useEffect(() => {
    if (!pendingActiveSelection) return;
    const { limit } = pendingActiveSelection;

    const current = selectedIds;
    const allowed = Math.max(0, limit - current.length);
    if (allowed <= 0) {
      setPendingActiveSelection(null);
      return;
    }

    const activeOptions = options.filter((opt) => opt.isActive !== false);
    const newSelections = activeOptions
      .filter((opt) => !current.includes(opt.value))
      .slice(0, allowed)
      .map((opt) => opt.value);

    if (newSelections.length > 0) {
      newSelections.forEach(onAdd);
      setPendingActiveSelection(null);
      return;
    }

    if (hasMore && !loadingMore) {
      onLoadMore();
      return;
    }

    setPendingActiveSelection(null);
  }, [pendingActiveSelection, options, selectedIds, hasMore, loadingMore, onLoadMore, onAdd]);

  // Default selection method based on existing selection
  useEffect(() => {
    if (selectionMethod === null) {
      const method = (selectedIds?.length || 0) > 0 ? 'selective' : 'active';
      try { dispatch(setSlackWizardSelectionMethod(method as any)); } catch (e) {}
    }
  }, [selectionMethod, selectedIds, dispatch]);

  return (
    <div className="space-y-4">
      {/* Radio group to choose All vs Specific */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Scope</label>
        </div>
        <div className="flex items-center gap-6">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="sm-scope"
              className="w-4 h-4 text-primary-light"
              checked={selectionMethod === 'active'}
              onChange={() => {
                if (selectionMethod !== 'active') {
                  // Preserve current selective selection before clearing
                  setPreviousSelectiveIds(selectedIds);
                  try { dispatch(setSlackWizardSelectionMethod('active')); } catch (e) {}
                  onClear();
                  setPendingActiveSelection({ limit: 30 });
                }
              }}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Active Strategy Maps</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="sm-scope"
              className="w-4 h-4 text-primary-light"
              checked={selectionMethod === 'selective'}
              onChange={() => {
                if (selectionMethod !== 'selective') {
                  try { dispatch(setSlackWizardSelectionMethod('selective')); } catch (e) {}
                  // Clear active auto-selection and restore previously chosen selective ids (if any)
                  onClear();
                  if (previousSelectiveIds?.length) {
                    previousSelectiveIds.forEach((id) => onAdd(id));
                  }
                  setPendingActiveSelection(null);
                }
              }}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Pick Specific Strategy Maps</span>
          </label>
        </div>

        {selectionMethod === 'active' && (
          <div className="mt-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 text-xs text-blue-700 dark:text-blue-300">
            <div className="flex items-start gap-2">
              <HiInformationCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="space-y-1.5 flex-1">
                <p>
                  Active strategy maps are those whose status is not <span className="font-medium">closed</span>.
                </p>
                <p className="text-blue-600 dark:text-blue-400">
                  A single consolidated report will be generated for the 30 most recent active strategy maps at the time of trigger and sent to your selected channel.
                </p>
              </div>
            </div>
          </div>
        )}

        {selectionMethod === 'selective' && (
          <>
            <div className="mt-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3 text-xs text-blue-700 dark:text-blue-300">
              <div className="flex items-start gap-2">
                <HiInformationCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <p>
                  A single consolidated report will be generated for all selected strategy maps and sent to your selected channel.
                </p>
              </div>
            </div>
            <div className="mt-4">
              <InfiniteScrollDropdown
                id="roadmap-picker"
                name="roadmap-picker"
                label="Strategy Maps"
                value=""
                placeholder="Search and select strategy maps..."
                onChange={handleEntitySelect}
                options={filteredOptions}
                loading={loading}
                loadingMore={loadingMore}
                hasMore={hasMore}
                onLoadMore={onLoadMore}
                onSearch={onSearch}
                required={true}
                showAsterisk={true}
              />
            </div>
          </>
        )}
      </div>

      {selectionMethod === 'selective' && !!visibleSelectedIds.length && (
        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="flex items-center gap-2 p-4 pb-3">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h4 className="text-sm font-semibold text-green-800 dark:text-green-300">
              {visibleSelectedIds.length} Strategy Map{visibleSelectedIds.length > 1 ? 's' : ''} Selected
            </h4>
          </div>
          <SelectedChips ids={visibleSelectedIds} getLabel={getLabel} onRemove={onRemove} scrollable={selectionMethod === 'selective'} />
        </div>
      )}
    </div>
  );
};

export default StrategyMapSelection;